import React, { useEffect, useRef, useState } from "react";
import Globe from "react-globe.gl";
import { Sidebar } from "./sidebar";
import earth_blue_marble from "../assets/earth-blue-marble.jpg";
import earth_topology from "../assets/earth-topology.png";
import night_sky from "../assets/night-sky.png";
import { socketIo } from "./socket";
import _, { debounce } from "lodash";
import StarEffect from "./StarEffect";

const socketHost = "nodered.greenheroes.co";
const socketPort = 39876;
const minuteMaxOutdated = 2;

export function Earth() {
    const globeRef = useRef(); // Reference to the Globe instance
    const [selectedPoint, setSelectedPoint] = useState(null); // State for the currently selected marker
    const selectedPointRef = useRef(selectedPoint); // Persistent reference to selected marker for socket updates

    const [rotation, setRotation] = useState(100); // Longitude for globe rotation
    const [latitude, setLatitude] = useState(0); // Latitude for globe view
    const [isUserInteracting, setIsUserInteracting] = useState(false); // Tracks if the user is interacting with the globe
    const [sidebarData, setSidebarData] = useState(null); // Data to display in the sidebar
    const interactionTimeoutRef = useRef(null); // Timeout reference for user interaction
    const initialZoomSet = useRef(false); // Tracks if initial zoom has been set
    const socketRef = useRef(null); // Persistent reference to socket connection
    const countOutdatedData = useRef(0); // Tracks the number of outdated data checks

    const [labelsRendered, setLabelsRendered] = useState(false); // Tracks if labels are rendered on the globe
    const [globeSize, setGlobeSize] = useState({ width: window.innerWidth, height: window.innerHeight }); // Tracks globe size for responsiveness
    const [markers, setMarkers] = useState([]); // List of markers to display on the globe

    // Establish and manage the WebSocket connection
    useEffect(() => {
        socketRef.current = socketIo(socketHost, socketPort); // Initialize socket connection
        const socket = socketRef.current;

        // Handle marker data received from the server
        const handleMarkers = (data) => setMarkers(data);

        // Handle the first set of data for a selected point
        const handleFirstTimesData = (data) => {
            if (data.success) {
                setSidebarData(data); // Set sidebar data
                countOutdatedData.current = 0; // Reset outdated data counter
            }
        };

        // Handle updates for the selected point
        const handleUpdate = (data) => {
            if (selectedPointRef.current?.measurement === data.measurement) {
                setSidebarData(data); // Update sidebar data
                countOutdatedData.current = 0;
            }
        };

        // Handle health check events to determine if data is outdated
        const handleHealthCheck = (data) => {
            if (selectedPointRef.current?.measurement === data.measurement) {
                if (countOutdatedData.current >= minuteMaxOutdated) {
                    setSidebarData(null); // Clear sidebar data if outdated
                } else {
                    countOutdatedData.current += 1;
                }
            }
        };

        // Add event listeners for socket messages
        socket.on("connect", () => socket.emit("get_markers")); // Request markers on connect
        socket.on("markers", handleMarkers); // Listen for marker updates
        socket.on("first_times_data", handleFirstTimesData);
        socket.on("update", handleUpdate);
        socket.on("health_check", handleHealthCheck);

        // Clean up socket listeners and disconnect on component unmount
        return () => {
            if (socketRef.current) {
                socket.off("markers", handleMarkers);
                socket.off("first_times_data", handleFirstTimesData);
                socket.off("update", handleUpdate);
                socket.off("health_check", handleHealthCheck);
                socket.disconnect();
            }
        };
    }, []);

    // Automatically rotate the globe if the user is not interacting
    useEffect(() => {
        const interval = setInterval(() => {
            if (!isUserInteracting && labelsRendered) {
                setRotation((prev) => (prev + 0.2) % 360); // Increment rotation
            }
        }, 30); // Rotate every 30ms
        return () => clearInterval(interval); // Clear interval on unmount
    }, [isUserInteracting, labelsRendered]);

    // Update the globe view when rotation or latitude changes
    useEffect(() => {
        if (globeRef.current) {
            const currentAltitude = globeRef.current.pointOfView().altitude; // Get current altitude
            globeRef.current.pointOfView({ lat: latitude, lng: rotation, altitude: currentAltitude }); // Update view
        }
    }, [rotation, latitude]);

    // Adjust globe size when the window resizes
    useEffect(() => {
        const handleResize = debounce(() => {
            setGlobeSize({ width: window.innerWidth, height: window.innerHeight }); // Update size
        }, 200); // Debounce to avoid frequent updates
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize); // Clean up listener
    }, []);

    // Set the initial zoom level for the globe
    useEffect(() => {
        if (globeRef.current && !initialZoomSet.current) {
            const altitude = window.innerWidth < 768 ? 5 : 3; // Use different zoom for mobile
            globeRef.current.pointOfView({ lat: latitude, lng: rotation, altitude });
            initialZoomSet.current = true; // Mark zoom as set
        }
    }, []);

    // Sync selectedPointRef with selectedPoint state
    useEffect(() => {
        selectedPointRef.current = selectedPoint;
    }, [selectedPoint]);

    // Delay label rendering to improve performance
    useEffect(() => {
        const timeout = setTimeout(() => setLabelsRendered(true), 1000); // Delay by 1s
        return () => clearTimeout(timeout); // Clear timeout on unmount
    }, []);

    // Track when the user starts interacting with the globe
    const handleUserInteractionStart = () => {
        setIsUserInteracting(true);
        if (interactionTimeoutRef.current) {
            clearTimeout(interactionTimeoutRef.current);
        }
    };

    // Track when the user stops interacting with the globe
    const handleUserInteractionEnd = () => {
        interactionTimeoutRef.current = setTimeout(() => setIsUserInteracting(false), 5000); // Reset after 5s
    };

    // Update rotation and latitude after interaction
    const handleRotationChange = () => {
        if (globeRef.current) {
            const { lng, lat } = globeRef.current.pointOfView(); // Get updated view
            setRotation(lng); // Update rotation
            setLatitude(lat); // Update latitude
        }
    };

    // Handle marker click to show data in sidebar
    const handlePointClick = (point) => {
        setSelectedPoint(point); // Set selected point
        const measurement = point.measurement;
        socketRef.current?.emit("realtime_first_times", { measurement }); // Request data for the marker
    };

    // Close the sidebar and clear selection
    const closeSidebar = () => {
        setSelectedPoint(null);
        setSidebarData(null);
    };

    // Adjust label size based on screen size
    const handleLabelSize = (d) => (globeSize.width < 768 ? d.labelSizeMobile || 1 : d.labelSizeDesktop || 1.5);

    // Adjust dot size based on screen size
    const handleDotRadius = (d) => (globeSize.width < 768 ? d.dotRadiusMobile || 0.3 : d.dotRadiusDesktop || 0.5);

    return (
        <div
            style={{ width: "100vw", height: "100%", display: "flex", position: "relative" }}
            onMouseDown={handleUserInteractionStart}
            onMouseUp={() => {
                handleUserInteractionEnd();
                handleRotationChange(); // Update rotation and latitude
            }}
            onTouchStart={handleUserInteractionStart}
            onTouchEnd={() => {
                handleUserInteractionEnd();
                handleRotationChange(); // Update rotation and latitude
            }}
            onClick={(e) => {
                if (!e.target.closest(".sidebar")) {
                    closeSidebar(); // Close sidebar if clicked outside
                }
            }}
        >
            <StarEffect />
            <Globe
                ref={globeRef}
                width={globeSize.width}
                height={globeSize.height}
                globeImageUrl={earth_blue_marble}
                bumpImageUrl={earth_topology}
                backgroundImageUrl={night_sky}
                enableZoom
                labelsData={markers}
                labelLat={(d) => d.lat}
                labelLng={(d) => d.lng}
                labelText={(d) => d.name}
                labelSize={handleLabelSize}
                labelColor={(d) => d.labelColor || "#fca900"}
                labelDotRadius={handleDotRadius}
                labelAltitude={() => 0.01}
                onLabelClick={handlePointClick}
            />
            {selectedPoint && <Sidebar selectedPoint={selectedPoint} onClose={closeSidebar} sidebarData={sidebarData} />}
        </div>
    );
}
